package app.footyguide.web

import app.footyguide.core.locale.BroadcastMarket
import app.footyguide.core.locale.LocalBroadcastMarket
import app.footyguide.core.locale.LocalStrings
import app.footyguide.core.locale.Strings
import app.footyguide.core.locale.UiLanguage
import app.footyguide.web.locale.WebLocale
import app.footyguide.web.theme.FootyTheme
import app.footyguide.web.data.UserRepository
import app.footyguide.web.data.WebChannelRepository
import app.footyguide.web.data.WebCompetitionRepository
import app.footyguide.web.data.WebGameRepository
import app.footyguide.web.data.WebTeamRepository
import app.footyguide.web.page.HomePage
import app.footyguide.web.page.GameDetailPage
import app.footyguide.web.page.MyTeamPage
import app.footyguide.web.page.ClubDetailPage
import app.footyguide.web.page.StandingsPage
import app.footyguide.web.page.ChannelsPage
import app.footyguide.web.components.TeamSelectionModal
import app.footyguide.web.route.WebRoute
import app.footyguide.web.route.WebRoutes
import app.footyguide.web.route.parseWebRoute
import app.footyguide.web.route.navigateTo
import app.footyguide.web.route.toPath
import app.footyguide.web.route.WebNavigationStack
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.css.*
import androidx.compose.runtime.*

private val gameRepository = WebGameRepository()
private val teamRepository = WebTeamRepository()
private val competitionRepository = WebCompetitionRepository()
private val channelRepository = WebChannelRepository()

fun main() {
    registerGlobalJs()
    registerServiceWorker()
    FootyTheme.applyStored()

    renderComposable(rootElementId = "root") {
        AppRoot()
    }
}

@Composable
fun AppRoot() {
    var currentPath by remember { mutableStateOf(window.location.pathname) }
    var showGlobalTeamModal by remember { mutableStateOf(false) }
    var refreshKey by remember { mutableStateOf(0) }

    LaunchedEffect(Unit) {
        WebNavigationStack.init(window.location.pathname)
        try {
            val standingsDoc = app.footyguide.web.data.FirestoreSdk.document("standings/brasileirao")
            val serverUpdatedAt = standingsDoc?.string("updatedAt") ?: standingsDoc?.string("lastUpdate")
            if (!serverUpdatedAt.isNullOrBlank() && app.footyguide.web.data.LocalCache.syncGlobalLastUpdate(serverUpdatedAt)) {
                app.footyguide.web.data.WebStandingsRepository.clearCache()
                refreshKey++
            }
        } catch (_: Throwable) {}
    }

    val navigate: (String) -> Unit = { path ->
        val cleanPath = if (path.startsWith("/")) path else "/$path"
        WebNavigationStack.push(cleanPath)
        if (cleanPath == WebRoutes.MY_TEAM || cleanPath == "${WebRoutes.MY_TEAM}/") {
            val savedTeam = window.localStorage.getItem("fg-my-team")
            if (savedTeam.isNullOrBlank()) {
                showGlobalTeamModal = true
            } else {
                val target = WebRoutes.myTeam(savedTeam)
                if (window.location.pathname != target) {
                    window.history.pushState(js("{}"), "", target)
                }
                currentPath = target
                window.scrollTo(0.0, 0.0)
            }
        } else {
            if (window.location.pathname != cleanPath) {
                window.history.pushState(js("{}"), "", cleanPath)
            }
            currentPath = cleanPath
            window.scrollTo(0.0, 0.0)
        }
    }

    val handleBack: () -> Unit = {
        val previous = WebNavigationStack.pop()
        val target = if (previous != null && previous != currentPath) previous else WebRoutes.HOME
        if (window.location.pathname != target) {
            window.history.pushState(js("{}"), "", target)
        }
        currentPath = target
        window.scrollTo(0.0, 0.0)
    }

    DisposableEffect(Unit) {
        val listener = { _: dynamic ->
            currentPath = window.location.pathname
            try {
                androidx.compose.runtime.snapshots.Snapshot.sendApplyNotifications()
            } catch (_: Throwable) {}
        }
        window.addEventListener("popstate", listener)
        window.addEventListener("hashchange", listener)
        window.addEventListener("pageshow", listener)
        window.asDynamic().__fgNavigate = { path: String ->
            navigate(path)
        }
        onDispose {
            window.removeEventListener("popstate", listener)
            window.removeEventListener("hashchange", listener)
            window.removeEventListener("pageshow", listener)
            window.asDynamic().__fgNavigate = null
        }
    }

    val route = parseWebRoute(currentPath)

    val homeDate = when (route) {
        is WebRoute.GamesByDate -> route.date
        is WebRoute.GameDetail -> null
        else -> null
    }

    val routeLanguage = when (route.locale) {
        "en" -> UiLanguage.EN
        "es" -> UiLanguage.ES
        else -> WebLocale.resolveLanguage()
    }
    val currentLanguage = remember(route.locale) { mutableStateOf(routeLanguage) }
    val strings = Strings.forLanguage(currentLanguage.value)
    val market = BroadcastMarket.default

    LaunchedEffect(currentPath) {
        val canonical = route.toPath()
        if (route is WebRoute.ClubDetail && currentPath != canonical && currentPath == "/${route.clubSlug}") {
            window.history.replaceState(js("{}"), "", canonical)
        }
    }

    CompositionLocalProvider(
        LocalStrings provides strings,
        LocalBroadcastMarket provides market
    ) {
        key(currentPath, refreshKey) {
            when (route) {
                is WebRoute.GameDetail -> {
                    val compositeGameId = if (!route.matchId.isNullOrBlank()) {
                        "${route.slug}/${route.matchId}"
                    } else {
                        route.slug
                    }
                    GameDetailPage(
                        gameId = compositeGameId,
                        gameRepository = gameRepository,
                        teamRepository = teamRepository,
                        channelRepository = channelRepository,
                        competitionRepository = competitionRepository,
                        onBack = handleBack
                    )
                }
                is WebRoute.MyTeam -> {
                    val resolvedTeamId = route.teamId ?: window.localStorage.getItem("fg-my-team")
                    if (resolvedTeamId.isNullOrBlank()) {
                        showGlobalTeamModal = true
                        HomePage(
                            gameRepository = gameRepository,
                            teamRepository = teamRepository,
                            competitionRepository = competitionRepository,
                            channelRepository = channelRepository,
                            today = todayFormatted(),
                            initialDate = homeDate,
                            onDateSelected = { date ->
                                navigate(WebRoutes.scheduleByDate(date))
                            },
                            onNavigate = navigate
                        )
                    } else {
                        MyTeamPage(
                            initialTeamId = resolvedTeamId,
                            gameRepository = gameRepository,
                            onNavigate = { navigate(it) },
                            onChangeTeam = { showGlobalTeamModal = true }
                        )
                    }
                }
                is WebRoute.ClubDetail -> {
                    ClubDetailPage(
                        clubSlug = route.clubSlug,
                        gameRepository = gameRepository,
                        onNavigate = { navigate(it) },
                        onBack = handleBack
                    )
                }
                is WebRoute.Standings -> {
                    StandingsPage(
                        gameRepository = gameRepository,
                        onNavigate = { navigate(it) }
                    )
                }
                is WebRoute.Channels -> {
                    ChannelsPage(
                        initialChannelId = route.channelId,
                        gameRepository = gameRepository,
                        onNavigate = { navigate(it) }
                    )
                }
                else -> {
                    HomePage(
                        gameRepository = gameRepository,
                        teamRepository = teamRepository,
                        competitionRepository = competitionRepository,
                        channelRepository = channelRepository,
                        today = todayFormatted(),
                        initialDate = homeDate,
                        onDateSelected = { date ->
                            navigate(WebRoutes.scheduleByDate(date))
                        },
                        onNavigate = navigate
                    )
                }
            }
        }

        if (showGlobalTeamModal) {
            TeamSelectionModal(
                serieAIds = WebTeamRepository.SERIE_A_IDS,
                teams = emptyMap(),
                currentTeamId = window.localStorage.getItem("fg-my-team"),
                onTeamSelected = { id ->
                    if (id.isNotBlank()) {
                        window.localStorage.setItem("fg-my-team", id)
                        navigate(WebRoutes.myTeam(id))
                    }
                    showGlobalTeamModal = false
                },
                onClose = { showGlobalTeamModal = false }
            )
        }
    }
}

private fun registerGlobalJs() {
    js("""
        window.footyguide = {
            navigateTo: function(path) {
                if (typeof window.__fgNavigate === 'function') {
                    window.__fgNavigate(path);
                } else {
                    if (!path.startsWith('/')) path = '/' + path;
                    history.pushState({}, '', path);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                }
            },
            setMyTeam: function(teamId) {
                localStorage.setItem('fg-my-team', teamId);
                if (typeof window.__fgNavigate === 'function') {
                    window.__fgNavigate('/meu-time/' + teamId);
                } else {
                    window.location.reload();
                }
            },
            clearMyTeam: function() {
                localStorage.removeItem('fg-my-team');
                window.location.reload();
            },
            toggleTheme: function() {
                var current = localStorage.getItem('fg-theme') || 'system';
                var next;
                if (current === 'dark') next = 'light';
                else if (current === 'light') next = 'dark';
                else next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
                localStorage.setItem('fg-theme', next);
                document.documentElement.setAttribute('data-theme', next === 'system' ? '' : next);
            }
        };
    """)
}

private fun registerServiceWorker() {
    js(
        """
        if ('serviceWorker' in navigator) {
            window.addEventListener('load', function() {
                navigator.serviceWorker.register('/service-worker.js')
                    .catch(function(error) {
                        console.error('[PWA] service worker registration failed:', error);
                    });
            });
        }
        """
    )
}

fun todayFormatted(): String =
    js("new Date().toLocaleDateString('sv-SE')") as String
